/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo } from "react";
import { useApiContext } from 'context/ApiContext';  // Utilisation du contexte

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
    useMaterialUIController,
    setMiniSidenav,
    setTransparentSidenav,
    setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
    const location = useLocation();
    const { pathname } = location;
    const collapseName = location.pathname.replace("/", "");

    const userRole = localStorage.getItem('userRole');

    let textColor = "white";

    if (transparentSidenav || (whiteSidenav && !darkMode)) {
        textColor = "dark";
    } else if (whiteSidenav && darkMode) {
        textColor = "inherit";
    }

    const closeSidenav = () => setMiniSidenav(dispatch, true);

    useEffect(() => {
        // A function that sets the mini state of the sidenav.
        function handleMiniSidenav() {
            setMiniSidenav(dispatch, window.innerWidth < 1200);
            setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
            setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
        }

        /** 
         The event listener that's calling the handleMiniSidenav function when resizing the window.
        */
        window.addEventListener("resize", handleMiniSidenav);

        // Call the handleMiniSidenav function to set the state with the initial value.
        handleMiniSidenav();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleMiniSidenav);
    }, [dispatch, location]);


    const { allowedTopTypes, allowedBottomTypes } = useMemo(() => {
        let allowedTop = [];
        let allowedBottom = [];

        if (((pathname.includes("/parkList") || pathname.includes("/statistics") || pathname.includes("/administration"))) &&
            ((userRole === "AdminAT") || (userRole === "AdminEnterprise"))) {
            allowedTop = ["topPaL", "topLimitedPaL"];
        } else if (((pathname.includes("/parkList") || pathname.includes("/statistics"))) && ((userRole === "AdminPark"))) {
            allowedTop = ["topPaL"];
        } else if (((pathname.includes("/parkAdministration") || pathname.includes("/parkDashboard") || pathname.includes("/parkStatistics")))) {
            allowedTop = ["topPaD"];
            allowedBottom = ["bottomMULTI"];
        } else if (((pathname.includes("/turbineDashboard") || pathname.includes("/turbineLiveCameraView") 
            || pathname.includes("/turbineStatistics")))) {
            allowedTop = ["topTuD"];
            allowedBottom = ["bottomTuD", "bottomMULTI"];
        }

        return { allowedTopTypes: allowedTop, allowedBottomTypes: allowedBottom };
    }, [pathname, userRole]);

    const filterTopRoutes = route => allowedTopTypes.includes(route.type);
    // Filtrer les routes de type "collapse" (partie sup�rieure)
    const renderTopRoutes = routes.filter(filterTopRoutes).map(({ name, icon, key, route }) => (
        <NavLink key={key} to={route}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
    ));

    const filterBottomRoutes = route => allowedBottomTypes.includes(route.type);
    // Filtrer les routes de type "backTD" (partie inf�rieure)
    const renderBottomRoutes = routes.filter(filterBottomRoutes).map(({ name, icon, key, route }) => (
        <NavLink key={key} to={route}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
    ));


    return (
        <SidenavRoot
            {...rest}
            variant="permanent"
            ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
        >
            <MDBox pt={4} pb={1} px={4} textAlign="center">
                <MDBox
                    display={{ xs: "block", xl: "none" }}
                    position="absolute"
                    top={0}
                    right={0}
                    p={1.625}
                    onClick={closeSidenav}
                    sx={{ cursor: "pointer" }}
                >
                    <MDTypography variant="h6" color="secondary">
                        <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                    </MDTypography>
                </MDBox>
                <MDBox alignItems="center">
                    {brand && (
                        <NavLink to="/parkList">
                            <MDBox
                                component="img"
                                src={brand}
                                alt="Brand"
                                width="8rem"
                                sx={{ cursor: "pointer" }}
                            />
                        </NavLink>
                    )}
                </MDBox>
            </MDBox>

            <Divider
                light={
                    (!darkMode && !whiteSidenav && !transparentSidenav) ||
                    (darkMode && !transparentSidenav && whiteSidenav)
                }
            />

            <List>{renderTopRoutes}</List>
            <MDBox position="absolute" bottom={0} width="100%" mb={3}>
                <List>{renderBottomRoutes}</List>
            </MDBox>
        </SidenavRoot>
    );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
    color: "info",
    brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    brand: PropTypes.string,
    //brandName: PropTypes.string.isRequired,
    routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
